import {
  EMPTY_ARRAY,
  ProductTypeEnum,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  type Security,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { toMarketSelectorValue } from '../MarketSelector/utils/marketSelectorValue';
import type { MultilegMarketSelectorLegButton } from './MultilegMarketSelector';
import type { MultiLegSummaryProps } from './types';

// Existing multileg callbacks use -1 for the Unified Liquidity leg. Keep the market selector switcher on the same convention.
export const MULTILEG_UNIFIED_LIQUIDITY_LEG_INDEX = -1;

type MultilegLegDetails = NonNullable<NonNullable<Security['MultilegDetails']>['Legs']>;

/**
 * Builds the optional in-panel leg switcher model shared by multileg market selectors.
 *
 * The UL entry intentionally uses the same sentinel as the rest of the multileg callbacks, so switching
 * from inside the panel can reuse the same change handling path as opening the selector from a leg row.
 */
export function useMultilegLegButtons({
  enabled = true,
  legDetails,
  resolvedLegMarkets,
  unifiedLiquidityMarkets,
  unifiedLiquidityDisplaySymbols,
  includeUnifiedLiquidity,
  showAllMarketsToggle = false,
}: {
  enabled?: boolean;
  legDetails: MultilegLegDetails;
  resolvedLegMarkets: string[][];
  unifiedLiquidityMarkets: MultiLegSummaryProps['unifiedLiquidityMarkets'];
  unifiedLiquidityDisplaySymbols: string;
  includeUnifiedLiquidity: boolean;
  /**
   * Only in certain cases can the user select "All Markets", which is represented by an empty selection. Setting this
   * to true puts is into a certain mode. In the order form for example, an empty selection is invalid, so this is not allowed.
   */
  showAllMarketsToggle?: boolean;
}): MultilegMarketSelectorLegButton[] | undefined {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();

  return useMemo(() => {
    if (!enabled) {
      return undefined;
    }

    const getSelectionDisplayNames = (selections: string[]) =>
      selections.map(selection => {
        return (marketsByName.get(selection) ?? marketAccountsByName.get(selection))?.DisplayName ?? selection;
      });

    const getSubText = ({ selections, canUseAllMarkets }: { selections: string[]; canUseAllMarkets: boolean }) => {
      const selectionDisplayNames = getSelectionDisplayNames(selections);
      const subTextItems =
        selectionDisplayNames.length > 0 ? selectionDisplayNames : [canUseAllMarkets ? 'All Markets' : 'No Markets'];

      return {
        subText: subTextItems.join(', '),
        subTextItems,
      };
    };

    const legButtons = legDetails.map((leg, index) => {
      const security = securitiesBySymbol.get(leg.Symbol);
      const selections = resolvedLegMarkets.at(index) ?? EMPTY_ARRAY;

      // Setting "All Markets" (which is represented by an empty selection) is only really worthwhile for Spot instruments where there will be
      // many markets applicable for the leg. For non-spot, eg perps, there is usually just one specific market applicable. So we don't show "All Markets" for non-spot.
      const canUseAllMarkets = showAllMarketsToggle && security?.ProductType === ProductTypeEnum.Spot;

      return {
        legIndex: index,
        displaySymbol: security?.DisplaySymbol ?? leg.Symbol,
        value: toMarketSelectorValue(selections),
        ...getSubText({ selections, canUseAllMarkets }),
      };
    });

    if (includeUnifiedLiquidity) {
      const selections = unifiedLiquidityMarkets ?? EMPTY_ARRAY;

      legButtons.push({
        legIndex: MULTILEG_UNIFIED_LIQUIDITY_LEG_INDEX,
        displaySymbol: unifiedLiquidityDisplaySymbols,
        value: toMarketSelectorValue(selections),
        ...getSubText({ selections, canUseAllMarkets: false }),
      });
    }

    return legButtons;
  }, [
    enabled,
    includeUnifiedLiquidity,
    legDetails,
    marketAccountsByName,
    marketsByName,
    resolvedLegMarkets,
    securitiesBySymbol,
    showAllMarketsToggle,
    unifiedLiquidityDisplaySymbols,
    unifiedLiquidityMarkets,
  ]);
}
